import { useNavigate } from "react-router-dom";
import { CheckIcon, ArrowRightIcon, CircleDotIcon, ChevronRightIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { t } from "@/lib/typography";
import type { BookListItem } from "@/types/book";

function getCompletedCount(book: BookListItem): number {
  return book.progress?.last_completed_chapter_order ?? 0;
}

function getNextChapterLabel(book: BookListItem): string | null {
  if (!book.progress) return null;
  const next = getCompletedCount(book) + 1;
  if (next > book.total_chapters) return null;
  return `Ch ${next}`;
}

export function BookCard({
  book,
  onStart,
  isStarting,
}: {
  book: BookListItem;
  onStart: () => void;
  isStarting: boolean;
}) {
  const navigate = useNavigate();
  const progress = book.progress;
  const completed = getCompletedCount(book);
  const isCompleted =
    progress?.completed_finish_assessment_at != null ||
    completed >= book.total_chapters;
  const progressPercent =
    book.total_chapters > 0 ? (completed / book.total_chapters) * 100 : 0;

  const handleClick = () => {
    if (progress) {
      navigate(`/books/${book.id}`);
    }
  };

  return (
    <Card
      className={`transition-all group ${progress ? "cursor-pointer hover:shadow-md hover:border-primary/50" : ""}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (progress && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleClick();
        }
      }}
      role={progress ? "link" : undefined}
      tabIndex={progress ? 0 : undefined}
      aria-label={
        progress ? `${isCompleted ? "Review" : "Continue"} ${book.title}` : undefined
      }
    >
      <CardContent className="flex gap-4 p-4 sm:gap-5 sm:p-5">
        {/* Cover thumbnail — fixed landscape slot */}
        {book.cover_image && (
          <img
            src={book.cover_image}
            alt={book.title}
            loading="lazy"
            className="w-[140px] sm:w-[180px] aspect-[3/2] object-contain bg-muted rounded-md shrink-0 self-start border border-border/50"
          />
        )}

        {/* Right column */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          {/* Title + Author */}
          <div className="min-w-0">
            <h3 className={`${t.sectionTitle} flex items-center gap-2 truncate`}>
              {isCompleted && (
                <CheckIcon className="size-5 text-green-600 dark:text-green-400 shrink-0" />
              )}
              <span className="truncate">{book.title}</span>
            </h3>
            <p className={`${t.meta} truncate`}>{book.author}</p>
          </div>

          {/* Progress or chapter count */}
          {progress ? (
            <div className="space-y-1.5">
              <Progress value={progressPercent} />
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className={t.meta}>
                  {completed} / {book.total_chapters} chapters
                </span>
                {progress.completed_starting_assessment_at ? (
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                    <CheckIcon className="size-3.5" />
                    <span className={t.caption}>Starting assessment done</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-primary">
                    <CircleDotIcon className="size-3.5" />
                    <span className={t.caption}>Take starting assessment</span>
                  </span>
                )}
              </div>
            </div>
          ) : (
            <p className={t.meta}>{book.total_chapters} chapters</p>
          )}

          {/* Action row — pushed to the bottom of the right column */}
          <div className="mt-auto flex items-center justify-between gap-3 pt-1">
            <div className="min-w-0">
              {isCompleted && progress?.completed_finish_assessment_at && (
                <span className={`${t.meta} truncate`}>
                  Completed{" "}
                  {new Date(progress.completed_finish_assessment_at).toLocaleDateString()}
                </span>
              )}
              {progress && !isCompleted && getNextChapterLabel(book) && (
                <span className={t.meta}>Next: {getNextChapterLabel(book)}</span>
              )}
            </div>

            {!progress ? (
              <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                <Button
                  onClick={onStart}
                  disabled={isStarting}
                  className="gap-1.5"
                >
                  Start Reading
                  <ArrowRightIcon className="size-4" />
                </Button>
              </div>
            ) : (
              <ChevronRightIcon
                aria-hidden
                className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
